import { useEffect, useRef, type MouseEvent } from "react";
import { Row } from "@tanstack/react-table";
import Icon from "@/components/Icon";
import { isCompactLayout } from "@/assets/theme/sizes";

interface ExpandableCountCellProps {
  row: Row<any>;
  count: number;
  total?: number;
  hasItems: boolean;
  showExpandIcon: boolean;
  className?: string;
  autoExpandRowId?: string | null;
  onAutoExpandComplete?: () => void;
  /** When set, overrides the default count / count-of-total label. */
  displayText?: string;
}

/**
 * Module-level component for expandable count cells. Defined in its own file
 * so React maintains a stable component identity across renders, which is
 * required for useEffect (auto-expand) to work reliably. `autoExpandRowId`
 * expands once per mounted instance. Parents that rebuild column defs each
 * render must clear it via `onAutoExpandComplete` so collapse is not undone
 * by a remount.
 */
export default function ExpandableCountCell({
  row,
  count,
  total,
  hasItems,
  showExpandIcon,
  className,
  autoExpandRowId,
  onAutoExpandComplete,
  displayText: displayTextOverride
}: ExpandableCountCellProps) {
  const compact = isCompactLayout();
  const shouldShowIcon = showExpandIcon && hasItems && !compact;
  const displayText = displayTextOverride ?? (total !== undefined ? `${count} of ${total}` : `${count}`);
  const autoExpandedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!autoExpandRowId) {
      autoExpandedIdRef.current = null;
      return;
    }

    if (autoExpandedIdRef.current === autoExpandRowId) {
      return;
    }

    if (autoExpandRowId === row.id && hasItems && !row.getIsExpanded()) {
      autoExpandedIdRef.current = autoExpandRowId;
      row.getToggleExpandedHandler()();
      onAutoExpandComplete?.();
    }
  }, [autoExpandRowId, row, hasItems, onAutoExpandComplete]);

  const cellClassName = `flex items-center gap-2 ${compact ? "w-auto" : "min-h-12 w-full -mx-4 -my-2 px-4 py-2"} ${className || ""}`;

  if (!hasItems) {
    return (
      <div className={cellClassName}>
        <span className={displayText === "--" ? "opacity-20" : undefined}>{displayText}</span>
      </div>
    );
  }

  const expanded = row.getIsExpanded();
  const toggleExpanded = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    row.getToggleExpandedHandler()();
  };

  return (
    <button
      type="button"
      className={`${cellClassName} cursor-pointer appearance-none border-0 bg-transparent font-inherit text-inherit text-start`}
      aria-expanded={expanded}
      aria-label={expanded ? `Collapse ${displayText}` : `Expand ${displayText}`}
      onClick={toggleExpanded}
    >
      <span>{displayText}</span>
      {shouldShowIcon && <Icon name={expanded ? "expand_less" : "expand_more"} />}
    </button>
  );
}
